import type { User as FirebaseUser } from 'firebase/auth';
import { User, UserType } from './types';

const TOKEN_KEY = 'authToken';
const USER_DATA_KEY = 'userData'; // Define key for user data

export const authUtils = {
  storeToken(token: string, expiresInSeconds: number = 3600): void {
    const MIN_EXPIRES = 3600; // seconds (1 hour)
    const finalExpires = Math.max(expiresInSeconds, MIN_EXPIRES);
    const expiry = Date.now() + finalExpires * 1000;
    localStorage.setItem(TOKEN_KEY, JSON.stringify({ token, expiry }));
    if (finalExpires !== expiresInSeconds) {
      console.warn(`Requested expiresInSeconds ${expiresInSeconds}s is less than minimum. Using ${finalExpires}s instead.`);
    }
    console.log(`Token stored with expiry in ${finalExpires} seconds`);
  },

  getToken(): { token: string; expiry: number } | null {
    const stored = localStorage.getItem(TOKEN_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse token from storage', e);
      localStorage.removeItem(TOKEN_KEY);
      return null;
    }
  },

  isTokenExpired(): boolean {
    const tokenData = this.getToken();
    return !tokenData || Date.now() >= tokenData.expiry;
  },

  clearTokens(): void {
    localStorage.removeItem(TOKEN_KEY);
    console.log('Auth token cleared from storage.');
  },

  // Store user data (assuming this exists or is needed)
  storeUserData(user: User): void {
    try {
      localStorage.setItem(USER_DATA_KEY, JSON.stringify(user));
      console.log('User data stored in storage.');
    } catch (e) {
      console.error('Failed to store user data', e);
    }
  },

  // Retrieve user data (assuming this exists or is needed)
  getUserData(): User | null {
    const stored = localStorage.getItem(USER_DATA_KEY);
    if (!stored) return null;
    try {
      return JSON.parse(stored);
    } catch (e) {
      console.error('Failed to parse user data from storage', e);
      localStorage.removeItem(USER_DATA_KEY);
      return null;
    }
  },

  // ADDED: Clear user data from local storage
  clearUserData(): void {
    localStorage.removeItem(USER_DATA_KEY);
    console.log('User data cleared from storage.');
  },

  validateUserType(userType: string | undefined | null): UserType {
    if (!userType) {
      console.warn(`Invalid user_type: undefined/null, defaulting to UNSUBSCRIBED`);
      return UserType.UNSUBSCRIBED;
    }
    if (Object.values(UserType).includes(userType as UserType)) {
      return userType as UserType;
    }
    console.warn(`Invalid user_type: ${userType}, defaulting to UNSUBSCRIBED`);
    return UserType.UNSUBSCRIBED;
  },

  hasPermission(userType: UserType | null | undefined, requiredTypes: UserType | UserType[]): boolean {
    if (!userType) return false;
    if (userType === UserType.BLOCKED) return false;
    if (userType === UserType.ADMIN) return true;
    if (Array.isArray(requiredTypes)) {
      return requiredTypes.includes(userType);
    }
    return userType === requiredTypes;
  }
};